import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, CURRENCY } from "@/lib/api";
import { track, gaItem } from "@/lib/analytics";

const StoreCtx = createContext(null);
const CART_KEY = "gf_cart";

const loadCart = () => {
  try { return JSON.parse(localStorage.getItem(CART_KEY)) || {}; } catch { return {}; }
};

const SHIP_KEY = "gf_ship_to";
const loadShip = () => {
  try { return JSON.parse(localStorage.getItem(SHIP_KEY)) || {}; } catch { return {}; }
};

export const StoreProvider = ({ children }) => {
  const [settings, setSettings] = useState(null);
  const [cart, setCart] = useState(loadCart);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartCountry, setCartCountry] = useState(() => loadShip().country || "US");
  const [cartState, setCartState] = useState(() => loadShip().state || "");
  useEffect(() => { localStorage.setItem(SHIP_KEY, JSON.stringify({ country: cartCountry, state: cartState })); }, [cartCountry, cartState]);

  const [member, setMember] = useState(false);
  const refresh = useCallback(() => api.get("/store").then((r) => setSettings(r.data)).catch(() => {}), []);
  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(cart)); }, [cart]);

  const products = settings?.products || [];
  const byId = Object.fromEntries(products.map((p) => [p.id, p]));
  const anyActive = products.some((p) => p.active);
  useEffect(() => {
    if (!settings) return;
    setCart((c) => Object.fromEntries(Object.entries(c).filter(([id]) => settings.products.some((p) => p.id === id && p.active))));
  }, [settings]);

  const maxFor = (id) => Math.min(20, byId[id]?.available ?? 0);
  const updateQty = (id, n) => setCart((c) => {
    const q = Math.max(0, Math.min(n, maxFor(id)));
    const next = { ...c };
    if (q) next[id] = q; else delete next[id];
    return next;
  });
  const addToCart = (id, n) => {
    const p = byId[id];
    const add = p ? Math.max(0, Math.min((cart[id] || 0) + n, maxFor(id)) - (cart[id] || 0)) : 0;
    updateQty(id, (cart[id] || 0) + n);
    setCartOpen(true);
    if (add > 0) track("add_to_cart", { currency: CURRENCY, value: Number((p.price_usd * add).toFixed(2)), items: [gaItem(p, add)] });
  };
  const clearCart = () => setCart({});
  const items = Object.entries(cart).filter(([id]) => byId[id]).map(([id, qty]) => ({ product: byId[id], qty }));
  const count = items.reduce((s, i) => s + i.qty, 0);

  return (
    <StoreCtx.Provider value={{ settings, products, byId, refresh, items, count, addToCart, updateQty, clearCart, maxFor, anyActive, cartOpen, setCartOpen, cartCountry, setCartCountry, cartState, setCartState, member, setMember }}>
      {children}
    </StoreCtx.Provider>
  );
};

export const useStore = () => useContext(StoreCtx);
